import {
  Button,
  cn,
  RadioGroup,
  RadioGroupItem,
  Tag,
  Typography,
} from "@suraasa/placebo-ui"
import { SuraasaPlusProduct } from "api/resources/suraasaPlus/types"
import RazorpayLogo from "assets/razorpayLogo.webp"
import StripeLogo from "assets/stripeLogo.webp"
import clsx from "clsx"
import AsyncBuilder from "components/AsyncBuilder"
import { createPortal } from "react-dom"
import { Link } from "react-router-dom"
import { getPlatformURL } from "utils/helpers"
import useFormatPrice from "utils/hooks/useFormatPrice"
import { trackingService } from "utils/tracking"
import { HubSpotTrackingEvents, TrackingEvents } from "utils/tracking/events"

import ProductBanner from "../../product-banner"
import {
  getPlanDisplayData,
  getPlanModeBySubscriptionIntervalType,
  PlanMode,
} from "../../utils"

const SelectPlan = ({
  checkout,
  className,
  selectedProduct,
  setSelectedPlan: setSelectedPlan,
  products,
}: {
  selectedProduct: SuraasaPlusProduct
  setSelectedPlan: (product: PlanMode) => void
  className?: string
  products: SuraasaPlusProduct[]
  checkout: () => void
}) => {
  const formatPrice = useFormatPrice()

  const getCurrencySymbol = (product: SuraasaPlusProduct) =>
    product.price.currency.symbol ?? ""

  const isIndia = selectedProduct.price.currency.code === "INR"

  const getPlan = (product: SuraasaPlusProduct) => {
    if (!product.subscriptionDetails) {
      throw new Error("Product does not have subscription details")
    }
    return product.subscriptionDetails?.intervalType
  }

  const selectedPlan = getPlan(selectedProduct)

  const promo = selectedProduct.price.promocode

  const finalPriceDisplay = (() => {
    const price = promo ? promo.priceAfterDiscount : selectedProduct.price.price
    return `${getCurrencySymbol(selectedProduct)}${formatPrice(price)}`
  })()

  const discountAmount = (
    selectedProduct.price.price - (promo?.priceAfterDiscount ?? 0)
  ).toFixed(1)

  const isSavingMoney =
    Boolean(selectedProduct.price.mrp - selectedProduct.price.price) || !!promo

  const PriceBreakdown = isSavingMoney ? (
    <>
      <div className="mb-1 flex items-center justify-between gap-1 text-onSurface-500">
        <Typography>Suraasa Plus Membership</Typography>
        <Typography variant="strong" className="shrink-0">
          {getCurrencySymbol(selectedProduct)}
          {formatPrice(selectedProduct.price.mrp)}
        </Typography>
      </div>
      <div className="mb-1 flex items-center justify-between gap-1 text-onSurface-500">
        <Typography>Discount</Typography>
        <Typography variant="strong" className="shrink-0 text-success-500">
          -{getCurrencySymbol(selectedProduct)}
          {formatPrice(selectedProduct.price.mrp - selectedProduct.price.price)}
        </Typography>
      </div>
      {promo && (
        <div className="mb-1 flex items-center justify-between gap-1 text-onSurface-500">
          <Typography>
            Promocode {'"'}
            {promo.code}
            {'"'} Applied
          </Typography>
          <Typography variant="strong" className="shrink-0 text-success-500">
            -{getCurrencySymbol(selectedProduct)}
            {discountAmount}
          </Typography>
        </div>
      )}
      <div className="mb-1 flex items-center justify-between gap-1 text-onSurface-500">
        <Typography>Total</Typography>
        <Typography variant="strong" className="shrink-0">
          {getCurrencySymbol(selectedProduct)}
          {formatPrice(
            promo ? promo.priceAfterDiscount : selectedProduct.price.price
          )}
        </Typography>
      </div>
    </>
  ) : null

  const PaymentButton = (
    <div
      className={clsx("flex flex-1 flex-col", {
        "z-10 fixed left-0 bottom-0 w-full px-2 py-1 sm:px-5 sm:py-2 bg-white border-t":
          true,
        "md:my-2 md:static md:px-0 md:py-0 md:bg-none md:border-t-0": true,
      })}
    >
      <div className="hidden border-t border-onSurface-200 pt-1 md:block">
        {PriceBreakdown}
      </div>

      <AsyncBuilder
        handler={async () => {
          triggerTrackingEvents({
            price: promo
              ? promo.priceAfterDiscount
              : selectedProduct.price.price,
            planMode: selectedProduct.subscriptionDetails
              ? getPlanModeBySubscriptionIntervalType(
                  selectedProduct.subscriptionDetails
                )
              : null,
            currency: selectedProduct.price.currency.code,
            id: selectedProduct.id,
          })
          await checkout()
        }}
        render={props => (
          <Button
            color="black"
            id="suraasa_plus_payment__Proceed_to_Pay"
            className="mt-0.5"
            onClick={props.onClick}
            loading={props.loading}
          >
            Proceed to Pay {finalPriceDisplay}
          </Button>
        )}
      />
      {isIndia ? (
        <div className="mt-1 flex flex-row items-center justify-center gap-0.5">
          <Typography variant={"smallBody"} className="text-onSurface-500">
            Secured by
          </Typography>
          <img src={RazorpayLogo} alt="Razorpay" className="h-2" />
        </div>
      ) : (
        <div className="mt-1 flex flex-row items-center justify-center gap-0.5">
          <Typography variant={"smallBody"} className="text-onSurface-500">
            Powered by
          </Typography>
          <img src={StripeLogo} alt="stripe" className="h-2" />
        </div>
      )}

      <Typography
        variant={"smallBody"}
        className="mt-0.5 text-center text-xs text-secondary-500"
      >
        By proceeding you agree to accept our
        <Link to={getPlatformURL("suraasa", "/terms-of-use")} target="_blank">
          <span className="text-interactive-500"> terms & conditions</span>
        </Link>{" "}
        and consent to account creation.
      </Typography>
    </div>
  )

  const submitButtonContainer = document.getElementById(
    "single-page-checkout-submit-button"
  )

  return (
    <div className={cn("flex flex-col gap-2", className)}>
      <ProductBanner
        className={clsx({
          "mb-5 hidden md:block": true,
        })}
      />

      {products.length === 0 && (
        // Skeleton UI
        <div className="flex flex-1 flex-col gap-2">
          <div className="h-6 w-1/3 animate-pulse rounded-md bg-onSurface-200"></div>
          <div className="h-4 w-2/3 animate-pulse rounded-md bg-onSurface-200"></div>
          <div className="h-20 w-full animate-pulse rounded-md bg-onSurface-200"></div>
        </div>
      )}

      {products.length === 1 && (
        <div className="flex flex-1 flex-col">
          <Typography variant={"title3"} className="mb-0.5">
            1-Year Membership
          </Typography>
          <Typography variant={"largeBody"} className="mb-2 text-onSurface-500">
            Unlimited access to benefits.
            <br />
            No auto-renewal.
          </Typography>

          <div className="mt-1 block md:hidden">{PriceBreakdown}</div>
          {!!submitButtonContainer &&
            createPortal(
              <span className="hidden md:block">{PaymentButton}</span>,
              submitButtonContainer
            )}
          <span className="mt-4 h-[200px] md:hidden">{PaymentButton}</span>
        </div>
      )}

      {products.length > 1 && (
        <div className="flex flex-1 flex-col">
          <Typography variant={"title3"} className="mb-0.5">
            Choose Your Plan
          </Typography>
          <Typography variant={"subtitle2"} className="mb-2 text-onSurface-500">
            Discount offer. Limited time only.
          </Typography>
          <RadioGroup
            value={selectedPlan}
            onValueChange={val => {
              setSelectedPlan(val as PlanMode)

              // trackingService.trackEvent(
              //   TrackingEvents.suraasaPlusPlanSelected,
              //   {
              //     plan_type: newProduct.subscriptionDetails?.intervalType,
              //     product_id: newProduct.id,
              //     price: newProduct.price?.price,
              //     currency: newProduct.price?.currency?.code,
              //   },
              //   { services: ["PostHog"] }
              // )
            }}
            className="flex flex-col gap-2.5 pb-3 pt-1 md:mb-6"
          >
            {products.map(product => {
              const details = product.subscriptionDetails
              if (!details) return null
              const config = getPlanDisplayData(details)
              const isSelected = selectedProduct.id === product.id

              const divisionFactor =
                config.planMode === PlanMode.MONTHLY
                  ? 1
                  : config.planMode === PlanMode.QUARTERLY
                  ? 3
                  : 12

              const price = product.price.price

              const mrp = product.price.mrp

              const subscriptionPeriod =
                config.planMode === PlanMode.MONTHLY
                  ? "1 month"
                  : config.planMode === PlanMode.QUARTERLY
                  ? "3 months"
                  : "1 year"

              const savings = mrp - price
              const currencySymbol = getCurrencySymbol(product)

              return (
                <button
                  key={product.id}
                  onClick={() => setSelectedPlan(config.planMode)}
                  className={clsx(
                    "flex cursor-pointer flex-col items-start justify-between rounded-xl bg-white p-2.5 outline outline-onSurface-300 transition-all duration-200 ease-in-out hover:shadow-md active:scale-95",
                    {
                      "outline-2 !outline-highlight-500": isSelected,
                    }
                  )}
                >
                  <div className="mb-1.5 flex w-full items-start justify-between">
                    <div className="flex flex-col items-start gap-0.25">
                      <Typography
                        variant="title3"
                        className="shrink-0 text-onSurface-700"
                      >
                        {config.label}
                      </Typography>
                      <Typography
                        variant="smallBody"
                        className="shrink-0 text-start !text-base text-onSurface-600"
                      >
                        {config.description}
                      </Typography>
                    </div>

                    <RadioGroupItem
                      className="size-2.5"
                      value={config.planMode}
                      checked={isSelected}
                    />
                  </div>

                  {savings > 0 && (
                    <div className="flex flex-wrap items-center gap-1">
                      <Typography
                        variant="title1"
                        className="shrink-0 text-start !text-[32px] font-bold"
                      >
                        {currencySymbol +
                          formatPrice(Math.round(price / divisionFactor))}
                      </Typography>
                      <div className="flex flex-row flex-wrap items-end gap-1">
                        <Typography
                          variant="title1"
                          className="shrink-0 bg-[linear-gradient(90deg,#B7B7B7_0%,#64748B_100%)] bg-clip-text !text-[32px] !font-normal text-transparent line-through decoration-red-500"
                        >
                          {currencySymbol +
                            formatPrice(Math.round(mrp / divisionFactor))}
                        </Typography>
                        <Typography
                          className="text-onSurface-400 md:mb-1"
                          variant={"body"}
                        >
                          /month
                        </Typography>
                      </div>
                      <Tag className="mb-1 rounded-lg bg-success-500 text-white md:mb-0">
                        {Math.round((savings / mrp) * 100)}% off
                      </Tag>
                    </div>
                  )}
                  <Typography className="text-onSurface-600" variant={"body"}>
                    {currencySymbol + formatPrice(price)} total for{" "}
                    {subscriptionPeriod}
                  </Typography>
                </button>
              )
            })}
          </RadioGroup>

          {promo && (
            <div className="mt-1 block md:hidden">{PriceBreakdown}</div>
          )}

          {!!submitButtonContainer &&
            createPortal(
              <span className="hidden md:block">{PaymentButton}</span>,
              submitButtonContainer
            )}
          <span className="mt-4 h-[200px] md:hidden">{PaymentButton}</span>
        </div>
      )}
    </div>
  )
}

export default SelectPlan

const triggerTrackingEvents = (product: {
  id: number
  price: number
  planMode: PlanMode | null
  currency: string
}) => {
  const data = {
    product_id: product.id,
    subscription_type: product.planMode,
    status: "initiated",
    plan_type: product.planMode,
    price: product.price,
    currency: product.currency,
  }

  // Track checkout initiation
  trackingService.trackEvent(
    TrackingEvents.suraasaPlusCheckoutInitiated,
    data,
    { services: ["PostHog"] }
  )

  trackingService.trackEvent(TrackingEvents.productPurchaseStatus, data, {
    services: ["GA"],
  })

  trackingService.trackEvent(
    HubSpotTrackingEvents.productPurchaseStatus,
    data,
    {
      services: ["HubSpot"],
    }
  )
}
